import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import * as TourOffersService from '~/services/TourOffersService';

import {
  TourOffer,
  TourOfferBookingTerms,
  TourOfferConfigurationContent,
  TourOfferConfigurationDetailsInput,
  TourOfferContent,
  TourOfferHotelAddOns,
  TourOfferItineraryInput,
  TourOfferNewDepartures,
  TourOfferPricingInput,
  TourOfferRelationshipsInput,
  TourOfferScheduleInput,
  TourOfferSummary,
  TourOfferTravellersInput,
} from '~/types/tours/tourOffer';

export const tourOfferQueryKeys = {
  all: ['tour-offers'] as const,
  lists: () => [...tourOfferQueryKeys.all, 'list'] as const,
  list: (search: string, ltleOnly: boolean) => [...tourOfferQueryKeys.lists(), search, ltleOnly] as const,
  details: () => [...tourOfferQueryKeys.all, 'detail'] as const,
  detail: (tourOfferId: string | undefined) => [...tourOfferQueryKeys.details(), tourOfferId ?? ''] as const,
  departures: (tourOfferId: string | undefined) =>
    [...tourOfferQueryKeys.all, 'departures', tourOfferId ?? ''] as const,
  offer: (tourOfferId: string | undefined) => [...tourOfferQueryKeys.all, 'offer', tourOfferId ?? ''] as const,
};

/** The categories and brands the native tour editor chooses from. */
export function useGetTourOfferFormOptions() {
  return useQuery({
    queryKey: [...tourOfferQueryKeys.all, 'form-options'] as const,
    queryFn: ({ signal }) => TourOffersService.getTourOfferFormOptions({ signal }),
    staleTime: Infinity,
  });
}

/** The payment schedule templates that fit the supplier payment due days. */
export function useGetTourOfferPaymentScheduleTemplates(tourOfferId: string, supplierPaymentDueDays: number | null) {
  return useQuery({
    queryKey: [...tourOfferQueryKeys.all, 'payment-schedule-templates', tourOfferId, supplierPaymentDueDays] as const,
    queryFn: ({ signal }) =>
      TourOffersService.getTourOfferPaymentScheduleTemplates(tourOfferId, supplierPaymentDueDays, { signal }),
    placeholderData: keepPreviousData,
  });
}

/** A legacy tour's native offer; searching by legacy tour id matches exactly. */
export function useGetTourOfferForTour(legacyTourId: string | undefined) {
  return useQuery({
    queryKey: [...tourOfferQueryKeys.all, 'for-tour', legacyTourId ?? ''] as const,
    queryFn: async ({ signal }) => {
      const offers = await TourOffersService.searchTourOffers({ search: legacyTourId }, { signal });
      return offers.find((offer) => offer.legacyTourId === legacyTourId) ?? null;
    },
    enabled: !!legacyTourId,
  });
}

/** Callers debounce, so each settled term is its own cache entry. */
export function useSearchTourOffers(search: string, ltleOnly: boolean) {
  return useQuery({
    queryKey: tourOfferQueryKeys.list(search, ltleOnly),
    queryFn: ({ signal }) => TourOffersService.searchTourOffers({ search: search || undefined, ltleOnly }, { signal }),
    placeholderData: keepPreviousData,
  });
}

/** There is no single-offer summary endpoint; searching by id matches exactly. */
const tourOfferSummaryQuery = (tourOfferId: string | undefined) => ({
  queryKey: tourOfferQueryKeys.detail(tourOfferId),
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const offers = await TourOffersService.searchTourOffers({ search: tourOfferId }, { signal });
    return offers.find((offer) => offer.id === tourOfferId) ?? null;
  },
  enabled: !!tourOfferId,
});

export function useGetTourOfferSummary(tourOfferId: string | undefined) {
  return useQuery(tourOfferSummaryQuery(tourOfferId));
}

type TourOfferSummaryResult = { data?: TourOfferSummary | null };

// Module level so React Query keeps the combined Map stable between renders.
const summariesById = (results: Array<TourOfferSummaryResult>) =>
  new Map(results.flatMap((result) => (result.data ? [[result.data.id, result.data] as const] : [])));

/** By tour offer id, holding those that have loaded. */
export function useGetTourOfferSummaries(tourOfferIds: Array<string>) {
  return useQueries({
    queries: tourOfferIds.map(tourOfferSummaryQuery),
    combine: summariesById,
  });
}

export function useGetTourOfferDepartures(tourOfferId: string | undefined) {
  return useQuery({
    queryKey: tourOfferQueryKeys.departures(tourOfferId),
    queryFn: ({ signal }) => TourOffersService.getTourOfferDepartures(tourOfferId, { signal }),
    enabled: !!tourOfferId,
  });
}

/** The whole tour offer, with its content, booking terms and configurations. */
export function useGetTourOffer(tourOfferId: string | undefined) {
  return useQuery({
    queryKey: tourOfferQueryKeys.offer(tourOfferId),
    queryFn: ({ signal }) => TourOffersService.getTourOffer(tourOfferId, { signal }),
    enabled: !!tourOfferId,
  });
}

interface TourOfferChanges {
  name?: string;
  status?: TourOffer['status'];
  /** A brand code; saved last, because moving to or from Luxury Escapes resets the fine print. */
  brand?: string;
  emailPreHeader?: string | null;
  newDepartures?: TourOfferNewDepartures;
  content?: TourOfferContent;
  bookingTerms?: TourOfferBookingTerms;
  /** By configuration id. */
  configurations?: Record<string, TourOfferConfigurationContent>;
  schedules?: Array<TourOfferScheduleInput>;
  relationships?: TourOfferRelationshipsInput;
  /** By configuration id. */
  configurationDetails?: Record<string, TourOfferConfigurationDetailsInput>;
  /** By configuration id. */
  configurationHotels?: Record<string, TourOfferHotelAddOns>;
  /** By configuration id; saved after content, since it can add and remove days. */
  configurationItineraries?: Record<string, TourOfferItineraryInput>;
  gallery?: Array<{ svcImageId: string; caption: string | null }>;
  travellers?: TourOfferTravellersInput;
  optionalExperiences?: Parameters<typeof TourOffersService.putTourOfferOptionalExperiences>[1];
}

/** Saves only the parts that changed; svc-tour takes each through its own endpoint. */
export function useSaveTourOffer(tourOfferId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      status,
      brand,
      emailPreHeader,
      newDepartures,
      content,
      bookingTerms,
      configurations = {},
      schedules,
      relationships,
      configurationDetails = {},
      configurationHotels = {},
      configurationItineraries = {},
      gallery,
      travellers,
      optionalExperiences,
    }: TourOfferChanges): Promise<TourOffer | undefined> => {
      let offer: TourOffer | undefined;
      if (name !== undefined || status !== undefined || emailPreHeader !== undefined || newDepartures !== undefined) {
        offer = await TourOffersService.updateTourOffer(tourOfferId, {
          name,
          status: status ?? undefined,
          emailPreHeader,
          newDepartures,
        });
      }
      if (content) {
        offer = await TourOffersService.putTourOfferContent(tourOfferId, content);
      }
      if (bookingTerms) {
        offer = await TourOffersService.putTourOfferBookingTerms(tourOfferId, bookingTerms);
      }
      for (const [configurationId, configuration] of Object.entries(configurations)) {
        offer = await TourOffersService.putTourOfferConfigurationContent(tourOfferId, configurationId, configuration);
      }
      for (const [configurationId, details] of Object.entries(configurationDetails)) {
        offer = await TourOffersService.putTourOfferConfigurationDetails(tourOfferId, configurationId, details);
      }
      for (const [configurationId, itinerary] of Object.entries(configurationItineraries)) {
        offer = await TourOffersService.putTourOfferConfigurationItinerary(tourOfferId, configurationId, itinerary);
      }
      for (const [configurationId, hotels] of Object.entries(configurationHotels)) {
        offer = await TourOffersService.putTourOfferConfigurationHotels(tourOfferId, configurationId, hotels);
      }
      if (gallery) {
        offer = await TourOffersService.putTourOfferGallery(tourOfferId, gallery);
      }
      if (travellers) {
        offer = await TourOffersService.putTourOfferTravellers(tourOfferId, travellers);
      }
      if (optionalExperiences) {
        offer = await TourOffersService.putTourOfferOptionalExperiences(tourOfferId, optionalExperiences);
      }
      if (schedules) {
        offer = await TourOffersService.putTourOfferSchedules(tourOfferId, schedules);
      }
      if (relationships) {
        offer = await TourOffersService.putTourOfferRelationships(tourOfferId, relationships);
      }
      if (brand !== undefined) {
        offer = await TourOffersService.updateTourOffer(tourOfferId, { brand });
      }
      return offer;
    },
    onSuccess: (offer) => {
      if (offer) {
        queryClient.setQueryData(tourOfferQueryKeys.offer(tourOfferId), offer);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tourOfferQueryKeys.detail(tourOfferId) });
      queryClient.invalidateQueries({ queryKey: tourOfferQueryKeys.offer(tourOfferId) });
    },
  });
}

/** Copies another tour's content onto this one; the editor reloads from what comes back. */
export function useCopyTourOfferContent(tourOfferId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fromTourOfferId: string) => TourOffersService.copyTourOfferContent(tourOfferId, fromTourOfferId),
    onSuccess: (offer) => queryClient.setQueryData(tourOfferQueryKeys.offer(tourOfferId), offer),
  });
}

const departureItineraryKey = (tourOfferId: string, configurationId: string) =>
  [...tourOfferQueryKeys.all, 'departure-itineraries', tourOfferId, configurationId] as const;

/** A Salesforce tour option's departures and their own itineraries. */
export function useGetTourOfferDepartureItineraries(tourOfferId: string, configurationId: string) {
  return useQuery({
    queryKey: departureItineraryKey(tourOfferId, configurationId),
    queryFn: ({ signal }) =>
      TourOffersService.getTourOfferDepartureItineraries(tourOfferId, configurationId, { signal }),
  });
}

/** Saves one departure's own itinerary, or removes it with no days. */
export function useSaveTourOfferDepartureItinerary(tourOfferId: string, configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ departureId, itinerary }: { departureId: string; itinerary: TourOfferItineraryInput }) =>
      TourOffersService.putTourOfferDepartureItinerary(tourOfferId, configurationId, departureId, itinerary),
    onSuccess: (departures) =>
      queryClient.setQueryData(departureItineraryKey(tourOfferId, configurationId), departures),
  });
}

const pricingKey = (tourOfferId: string) => [...tourOfferQueryKeys.all, 'pricing', tourOfferId] as const;

/** A Salesforce tour's dynamic pricing, by level. */
export function useGetTourOfferPricing(tourOfferId: string) {
  return useQuery({
    queryKey: pricingKey(tourOfferId),
    queryFn: ({ signal }) => TourOffersService.getTourOfferPricing(tourOfferId, { signal }),
  });
}

/** Saves the pricing; svc-tour works the display and member prices out again. */
export function useSaveTourOfferPricing(tourOfferId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pricing: TourOfferPricingInput) => TourOffersService.putTourOfferPricing(tourOfferId, pricing),
    onSuccess: (pricing) => queryClient.setQueryData(pricingKey(tourOfferId), pricing),
  });
}

/** The tour form's "Apply deposit setting at vendor level". */
export function useApplyTourOfferDepositToBrand(tourOfferId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => TourOffersService.applyTourOfferDepositToBrand(tourOfferId),
    onSuccess: (offer) => queryClient.setQueryData(tourOfferQueryKeys.offer(tourOfferId), offer),
  });
}
